import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { GameProvider } from "./context/GameContext";
import { NotificationProvider } from "./context/NotificationContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import HomePage from "./pages/HomePage";
import OdinAIPage from "./pages/OdinAIPage";
import ExplorePage from "./pages/ExplorePage";
import ReelsPage from "./pages/ReelsPage";
import ProfilePage from "./pages/ProfilePage";
import MessagesPage from "./pages/MessagesPage";
import NotificationsPage from "./pages/NotificationsPage";
import SearchPage from "./pages/SearchPage";
import BookmarksPage from "./pages/BookmarksPage";
import SettingsPage from "./pages/SettingsPage";
import PostDetailPage from "./pages/PostDetailPage";
import TipSuccessPage from "./pages/TipSuccessPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import CreatorDashboardPage from "./pages/CreatorDashboardPage";
import WalletPage from "./pages/WalletPage";
import PremiumSuccessPage from "./pages/PremiumSuccessPage";
import AdsPage from "./pages/AdsPage";
import WorldMapPage from "./pages/WorldMapPage";
import ModerationPage from "./pages/ModerationPage";
import TreasuryPage from "./pages/TreasuryPage";
import BetaGatePage from "./pages/BetaGatePage";
import RegionMapPage from "./pages/RegionMapPage";
import MidgardRegionalMap from "./pages/MidgardRegionalMap";
import SettlementPage from "./pages/SettlementPage";
import LocationPage from "./pages/LocationPage";
import GamePage from "./pages/GamePage";
import GamePage2 from "./pages/GamePage2";
import EnhancedGamePage from "./pages/EnhancedGamePage";
import MinimalTestPage from "./pages/MinimalTestPage";
import SimpleGamePage from "./pages/SimpleGamePage";

// Layout
import MainLayout from "./components/layout/MainLayout";

import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { betaMode, betaLocked, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }

  // Beta gate: authenticated but not on the approved list
  if (betaLocked) {
    return <BetaGatePage pending={true} />;
  }

  // Beta gate: unauthenticated visitor, and not on an auth path
  const isAuthPath = location.pathname === '/auth' || location.pathname.startsWith('/auth/');
  if (betaMode && !user && !isAuthPath) {
    return <BetaGatePage pending={false} />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected Routes with Layout */}
      <Route path="/home" element={<ProtectedRoute><MainLayout><HomePage /></MainLayout></ProtectedRoute>} />
      <Route path="/odin" element={<ProtectedRoute><MainLayout><OdinAIPage /></MainLayout></ProtectedRoute>} />
      <Route path="/explore" element={<ProtectedRoute><MainLayout><ExplorePage /></MainLayout></ProtectedRoute>} />
      <Route path="/reels" element={<ProtectedRoute><MainLayout hideNav><ReelsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/profile/:username" element={<ProtectedRoute><MainLayout><ProfilePage /></MainLayout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MainLayout><MessagesPage /></MainLayout></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><MainLayout><NotificationsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><MainLayout><SearchPage /></MainLayout></ProtectedRoute>} />
      <Route path="/bookmarks" element={<ProtectedRoute><MainLayout><BookmarksPage /></MainLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><MainLayout><SettingsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/post/:postId" element={<ProtectedRoute><MainLayout><PostDetailPage /></MainLayout></ProtectedRoute>} />
      <Route path="/creator-dashboard" element={<ProtectedRoute><MainLayout><CreatorDashboardPage /></MainLayout></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><MainLayout><WalletPage /></MainLayout></ProtectedRoute>} />
      <Route path="/wallet/success" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/ads" element={<ProtectedRoute><MainLayout><AdsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/tip/success" element={<ProtectedRoute><TipSuccessPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
      <Route path="/premium/success" element={<ProtectedRoute><PremiumSuccessPage /></ProtectedRoute>} />
      
      {/* World/Game Routes - Travel/Navigation UI */}
      <Route path="/world" element={<ProtectedRoute><WorldMapPage /></ProtectedRoute>} />
      <Route path="/moderation" element={<ProtectedRoute><MainLayout><ModerationPage /></MainLayout></ProtectedRoute>} />
      <Route path="/treasury" element={<ProtectedRoute><MainLayout><TreasuryPage /></MainLayout></ProtectedRoute>} />
      <Route path="/world/midgard" element={<ProtectedRoute><MidgardRegionalMap /></ProtectedRoute>} />
      <Route path="/world/midgard/settlement/:settlementId" element={<ProtectedRoute><SettlementPage /></ProtectedRoute>} />
      <Route path="/world/midgard/settlement/:settlementId/location/:locationId" element={<ProtectedRoute><LocationPage /></ProtectedRoute>} />
      <Route path="/world/:realmId" element={<ProtectedRoute><RegionMapPage /></ProtectedRoute>} />
      
      {/* 3D Game World Routes - Enhanced architecture with scene management */}
      <Route path="/world/midgard/play" element={<ProtectedRoute><EnhancedGamePage /></ProtectedRoute>} />
      <Route path="/world/midgard/play/:settlementId" element={<ProtectedRoute><EnhancedGamePage /></ProtectedRoute>} />
      <Route path="/world/midgard/play/:settlementId/:locationId" element={<ProtectedRoute><EnhancedGamePage /></ProtectedRoute>} />
      
      {/* Simple 3D Game Route (legacy, kept for testing) */}
      <Route path="/world/simple-game" element={<ProtectedRoute><SimpleGamePage /></ProtectedRoute>} />
      
      {/* Minimal 3D Test Route */}
      <Route path="/world/test3d" element={<ProtectedRoute><MinimalTestPage /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <GameProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </GameProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
